import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { sheeridFormRedirectEvent } from '#pplx/frontend/apps/ask/events/sheerid/analytics.js';
import { useIsLikelyStudent } from '#pplx/frontend/apps/ask/hooks/student-hooks/useIsLikelyStudent.js';
import { getComputerOnboardingV2 } from '#pplx/frontend/apps/ask/onboarding-computer/v2/api/computerOnboardingV2Api.js';
import { useMultiStep } from '#pplx/frontend/apps/ask/providers/MultiStepProviderShared.js';
import { getComputerOnboardingV2QueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/onboardingKeys.js';
import { useStudentLoginSource } from '#pplx/frontend/apps/ask/subscription/hooks/useStudentLoginSource.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
const EDUCATION_OCCUPATIONS = new Set(['student', 'educator']);
const SheerIDModalDynamic = Dynamic(async () => import('#pplx/frontend/apps/ask/students/SheerIDModal.js').then((m) => ({
    default: m.SheerIDModal,
})));
const mapOriginToLocationSource = (origin, studentLoginSource) => studentLoginSource.isFromStudentCometGrowthCountry
    ? ModalOrigin.GROW_COMET_STUDENTS_COUNTRY
    : studentLoginSource.isFromStudentCometGrowth
        ? ModalOrigin.GROW_COMET_STUDENTS
        : studentLoginSource.isFromStudentLanding
            ? ModalOrigin.STUDENT_LANDING_PAGE
            : origin;
export const useStudentPaywall = ({ origin, customCallbacks, fromPaywall, openVisitorLoginUpsell, openPaywallUpsell, handleClickPersonal, }) => {
    const { $t } = useIntl();
    const isUserLoggedIn = useIsUserLoggedIn();
    const { track } = useInstrumentation();
    const { handleComplete } = useMultiStep();
    const { openModal } = useModalContext();
    const studentLoginSource = useStudentLoginSource();
    const searchParams = useSearchParams();
    const redirectParam = searchParams?.get('redirect');
    const { hasActiveSubscription } = useIsSubscribed();
    const { signals, isLoading } = useIsLikelyStudent({
        reason: 'combined_paywall',
    });
    const { data: computerOnboarding, isLoading: isComputerOnboardingLoading } = useQuery({
        queryKey: getComputerOnboardingV2QueryKey({ subscriptionTier: 'basic' }),
        queryFn: () => getComputerOnboardingV2({
            reason: 'combined-paywall',
            subscriptionTier: 'basic',
        }),
        // Other callers use verification callbacks only and should not wait on onboarding data.
        enabled: isUserLoggedIn && fromPaywall === true,
    });
    const occupation = computerOnboarding?.answer.steps.occupation.answer?.occupation;
    const hasEducationOccupation = occupation !== undefined && EDUCATION_OCCUPATIONS.has(occupation);
    const isLikelyStudent = Boolean(signals.isVerifiedStudent) ||
        Boolean(signals.isPossibleStudentByEmail) ||
        Boolean(signals.isPossibleStudentByIP) ||
        hasEducationOccupation;
    const handleVerificationSuccess = useCallback((verificationId) => {
        if (!verificationId) {
            return;
        }
        if (studentLoginSource.isFromStudentCometGrowth) {
            handleComplete();
            return;
        }
        if (fromPaywall && !hasActiveSubscription) {
            // only direct to checkout if no subscription yet
            handleClickPersonal({
                billingPeriod: 'yearly',
                subscriptionTier: 'pro',
                origin,
            });
            return;
        }
        setTimeout(() => {
            openPaywallUpsell({
                origin: ModalOrigin.STUDENT_VERIFICATION_PAYWALL,
                pitchMessage: {
                    title: $t({
                        defaultMessage: "You're now eligible for Pro at a discount",
                        id: '722893cb66',
                        description: 'Pitch message message in the use Student Paywall subscription section',
                    }),
                },
            });
        }, 150);
    }, [
        fromPaywall,
        handleClickPersonal,
        handleComplete,
        openPaywallUpsell,
        $t,
        studentLoginSource.isFromStudentCometGrowth,
        hasActiveSubscription,
        origin,
    ]);
    const handleVerificationClick = useCallback((type) => {
        track(sheeridFormRedirectEvent({
            name: 'sheerid form redirect',
            data: {
                verificationLocationSource: mapOriginToLocationSource(origin, studentLoginSource),
                verificationRoleType: type,
            },
        }));
        openModal(SheerIDModalDynamic, {
            type,
            onVerificationSuccess: customCallbacks?.onSuccess ?? handleVerificationSuccess,
            onClose: customCallbacks?.onClose,
        }, { replace: false });
    }, [handleVerificationSuccess, origin, track, studentLoginSource, openModal, customCallbacks]);
    const handleStudentTierClick = useCallback((type) => {
        if (!isUserLoggedIn) {
            openVisitorLoginUpsell({
                title: $t({
                    defaultMessage: 'Sign in to unlock Education Pro',
                    id: '4e98c900d7',
                    description: 'Text in the use Student Paywall subscription section',
                }),
                origin: studentLoginSource.loginSource ?? origin,
                disallowedMethods: ['apple'],
                overrideRedirectUrl: redirectParam ?? undefined,
            });
            return;
        }
        handleVerificationClick(type);
    }, [
        isUserLoggedIn,
        handleVerificationClick,
        openVisitorLoginUpsell,
        $t,
        studentLoginSource.loginSource,
        origin,
        redirectParam,
    ]);
    // Don't default to the student/education segment for users who already have an active
    // subscription (e.g., Pro users). The student segment only shows Pro plans, so defaulting
    // to it prevents subscribed users from seeing the Max upgrade they actually need (e.g.,
    // when hitting an upload rate limit). Student login flows are still respected since
    // those indicate an explicit intent to use the student path.
    const shouldDefaultToStudent = studentLoginSource.isFromStudentFlow || (!hasActiveSubscription && isLikelyStudent);
    return {
        isLoading: Boolean(isLoading || isComputerOnboardingLoading),
        isLikelyStudent,
        isVerifiedStudent: Boolean(signals.isVerifiedStudent),
        handleStudentTierClick,
        handleVerificationSuccess,
        shouldDefaultToStudent,
        signals,
    };
};
//# sourceMappingURL=useStudentPaywall.js.map